import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  GetInsuranceReviewForVerification_getInsuranceReviewForVerification_pagination,
  GetInsuranceReviewForVerification_getInsuranceReviewForVerification_insurances,
  getFormattedDateAndTime,
} from '@modmd/data'
import { DEVICE, pxToRem } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { ConfirmInsurance } from 'internal-portal/routes/components/ConfirmInsurance'
import { Dialog } from 'components/Dialog'
import { AddStaffVerifier } from 'internal-portal/routes/InsuranceReviewVerification/AddStaffVerifier'

interface ColumnLabels {
  patient?: string | null
  barcode?: string | null
  result?: string | null
  testCategory?: string | null
  testSessionName?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  data: GetInsuranceReviewForVerification_getInsuranceReviewForVerification_insurances[]
  refetchData?: () => void
  pagination?: GetInsuranceReviewForVerification_getInsuranceReviewForVerification_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (
    data: GetInsuranceReviewForVerification_getInsuranceReviewForVerification_insurances
  ) => void
}

const defaultColumnLabels = {
  name: 'Name',
  birthDate: 'Birthdate',
  actions: 'Actions',
}

const PAGE_LENGTH = 10

export const InsuranceReviewVerificationDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  data,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  refetchData,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(
    () => `repeat(${numOfColumns}, 1fr)`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numOfColumns]
  )

  const [confirmationInsuranceModal, setConfirmInsuranceModal] = React.useState(false)
  const [insuranceUserId, setInsuranceUserId] = React.useState(0)

  const handleConfirmInsuranceModal = (open: boolean, userId: number) => {
    setInsuranceUserId(userId)
    setConfirmInsuranceModal(open)
  }

  const closeConfirmInsuranceModal = (open: boolean) => {
    if (refetchData) refetchData()
    setConfirmInsuranceModal(open)
  }

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={data}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.name) ? (
                <Datalist.HeaderCell label={columnLabels.name} />
              ) : null}
              {!isNull(columnLabels.birthDate) ? (
                <Datalist.HeaderCell label={columnLabels.birthDate} />
              ) : null}
              {!isNull(columnLabels.actions) ? (
                <Datalist.HeaderCell label={columnLabels.actions} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { User } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>
                    {User && (
                      <>
                        {User.firstName} {User.lastName}
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.birthDate) ? (
                  <Datalist.Cell label={columnLabels.birthDate}>
                    {getFormattedDateAndTime(User.birthDate)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.actions) ? (
                  <Datalist.Cell label={columnLabels.actions}>
                    <AddStaffVerifier
                      onClick={() => handleConfirmInsuranceModal(true, Number(data.userId))}
                    />
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
      <Dialog
        maxWidth={pxToRem(1000)}
        isOpen={confirmationInsuranceModal}
        onDismiss={() => {
          closeConfirmInsuranceModal(false)
        }}
      >
        <ConfirmInsurance
          setConfirmInsuranceModal={closeConfirmInsuranceModal}
          insuranceUserId={insuranceUserId}
        />
      </Dialog>
    </>
  )
}
